<template>
  <div class="prb-section-container">
    <BuilderHeader
      :active="active"
      :finishedSection="finishedSection"
      :index="index"
      :incomplete="incomplete"
      header="Offer Type & Delivery"
      section="offer-type"
      :changeActiveStepIdx="changeActiveStepIdx"
    />
    <div :class="['pbs-body-container offer-type date', active ? 'active' : null]">
      <div class="sac__font-16">When should this promotion be activated?</div>
      <div class="pbs-body">Date Range</div>
      <div class="promo-select-date-container">
        <div class="promo-select-date-label">Start Date:</div>
        <DatePicker v-model="startDate" class="pbs-date-picker" :min-date="today" />
      </div>
      <div class="promo-select-date-container" v-if="!isActive">
        <div class="promo-select-date-label">End Date:</div>
        <DatePicker v-model="endDate" class="pbs-date-picker" :min-date="endDateMin" />
      </div>
      <div class="flex-row align-center mb-3">
        <SACCheckbox :checked="isActive" :change="() => handleChange('isActive', !isActive)" />
        <span class="sac__font-14">This offer is ongoing (don't show expiration date)</span>
      </div>
      <div class="pbs-body">When offer becomes active:</div>
      <div class="pbs-radio-container">
        <input
          type="checkbox"
          class="diabled-check-input"
          onclick="return false;"
          :checked="showInApp"
          @change="handleChange('showInApp', $event.target.checked)"
        />
        <label for="show-in-app" class="promo-checkbox-label"> Show in-app (required) </label>
      </div>
      <div class="promo-set-time-container ml-4 mt-2">
        <div class="promo-set-time-label">Send on:</div>
        <select
          v-model="hour"
          disabledAdd
          commentMore
          actions
          class="promo-minute-input"
          aria-label="hour"
        >
          <option v-for="hourOption in hourOptions" :key="hourOption" :value="hourOption">
            {{ hourOption }}
          </option>
        </select>
        <div style="margin-right: 10px">:</div>
        <select v-model="minute" class="promo-minute-input" aria-label="minute">
          <option v-for="minOption in minOptions" :key="minOption" :value="minOption">
            {{ minOption }}
          </option>
        </select>
        <select v-model="meridiem" class="promo-minute-input" aria-label="meridiem">
          <option value="am">AM</option>
          <option value="pm">PM</option>
        </select>
        <select v-model="sendTimezone" disabled class="promo-select" aria-label="timezone">
          <option value="">Select Timezone</option>
          <option v-for="tz in timezoneOptions" :key="tz.label" :value="tz.value">
            {{ tz.label }}
          </option>
        </select>
      </div>
      <div class="promo-select-date-detail-txt ml-4">
        *Members may opt out of notifications via their app or phone OS settings. In-app promotions
        will still be displayed.
      </div>

      <div v-if="showNotification" class="pbs-body mb-1 mt-4">
        Sent out notification to members:
      </div>
      <div v-if="showNotification" class="flex-column ml-4">
        <div class="flex-row">
          <SACToggleSwitch
            :checked="sendPush"
            :change="() => handleChange('sendPush', !sendPush)"
          />
          <div class="sac__font-14 ml-2">Yes, send notifications to all available members.</div>
        </div>
        <span class="sac__font-16 mt-2 flex-row"
          >Potential members available:
          <div v-if="loading" class="loading-container">
            <img src="@shared/assets/img/general/loading.svg" height="22" width="22" />
          </div>
          <b v-else>{{ potentialMembers }}/{{ allMembers }}</b></span
        >
        <span class="sac__font-12 mt-2">
          Promo notifications are limited for homeowners to prevent over-saturation. The limits are:
        </span>
        <span class="sac__font-12 ml-2"> • {{ quarterlyLimit * 4 }} per year max. </span>
        <span class="sac__font-12 ml-2"> • {{ quarterlyLimit }} per quarter. </span>
      </div>

      <div class="pbs-body mb-0 mt-4">Include offer on the following plans:</div>
      <div class="pbs-subbody">
        When selected, members on these plans will be shown the offer in their smartphone app and
        Monthly Health Report email.
      </div>
      <div v-for="el in sortedPlans" class="flex-row align-center mb-1">
        <SACCheckbox
          :checked="selectedPlans?.includes(el.planId)"
          :change="() => togglePlan(el.planId)"
        />
        <span class="sac__font-14"
          >{{ el.name }} ({{ el.planType === 'Hardware' ? 'Detect' : 'Connect' }})</span
        >
      </div>

      <span v-if="errObj.wrongDate" class="error-message">
        {{ errObj.wrongDate }}
      </span>
      <div class="pbs-button-container">
        <button
          type="button"
          class="pbs-grey-button"
          :disabled="selectedPlans?.length == 0"
          @click="handleContinue"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import moment from 'moment-timezone';
import { mapState } from 'vuex';
import _ from 'lodash';

import promoMixin from '@shared/mixins/promoMixin';
import SACToggleSwitch from '@shared/components/sac/SACToggleSwitch.vue';

import SACCheckbox from '@shared/components/sac/SACCheckbox.vue';
import DatePicker from '@shared/components/DatePicker.vue';
import BuilderHeader from './BuilderHeader.vue';

export default {
  name: 'OfferType',
  components: {
    DatePicker,
    BuilderHeader,
    SACCheckbox,
    SACToggleSwitch,
  },
  mixins: [promoMixin],
  props: {
    index: Number,
    active: Boolean,
    promo: Object,
    partnerSettings: Object,
    changeActiveStepIdx: Function,
    finishedSection: Array,
    updatePromo: Function,
    setSectionIncomplete: Function,
  },
  data() {
    return {
      startDate: new Date(),
      selectedPlans: [],
      endDate: moment().add(1, 'days').toDate(),
      showInApp: true,
      sendPush: false,
      sendEmail: false,
      isActive: false,
      today: new Date(),
      sendTimezone: '',
      hour: '12',
      minute: '00',
      meridiem: 'pm',
      errObj: {},
      loading: false,
    };
  },
  computed: {
    ...mapState({
      plans: (state) => state.providerPlan.plans,
    }),
    allMembers() {
      return this.selectedPlans
        .map((e) => this.plans.find((x) => x.planId === e)?.activeMembers)
        .reduce((a, b) => a + b, 0);
    },
    potentialMembers() {
      return this.potentialUsers
        ?.filter((x) => this.selectedPlans.includes(x.planId))
        .map((x) => x.count)
        .reduce((a, b) => a + b, 0);
    },
    showNotification() {
      const { NOTIFICATIONS_ALLOWED = false } = this.partnerSettings;
      return NOTIFICATIONS_ALLOWED;
    },
    quarterlyLimit() {
      const { PROMO_QUARTERLY_LIMIT = 1 } = this.partnerSettings;
      return PROMO_QUARTERLY_LIMIT;
    },
    incomplete() {
      const { startDate, endDate, sendTime, sendPush, sendEmail, sendTimezone } = this.promo;
      let incomplete = false;
      if (sendTime) {
        const { meridiem, hour, minute } = this.calculateSendTime(sendTime);
        incomplete = this.hour !== hour || this.minute !== minute || this.meridiem !== meridiem;
      }
      if (
        startDate &&
        moment(startDate).format('YYYY-MM-DD') !== moment(this.startDate).format('YYYY-MM-DD')
      ) {
        incomplete = true;
      }
      if (
        endDate &&
        moment(endDate).format('YYYY-MM-DD') !== moment(this.endDate).format('YYYY-MM-DD')
      ) {
        incomplete = true;
      }
      if (sendPush !== this.sendPush) {
        incomplete = true;
      }
      if (sendEmail !== this.sendEmail) {
        incomplete = true;
      }
      if (sendTimezone) {
        incomplete = sendTimezone !== this.sendTimezone;
      } else if (!incomplete) {
        let currentTz = 'America/Chicago';
        const webTz = moment.tz.guess();
        const isInAmerica = this.timezoneOptions.find(({ value }) => value === webTz);
        if (isInAmerica) {
          currentTz = isInAmerica.value;
        }
        incomplete = currentTz !== this.sendTimezone;
      }
      return incomplete;
    },
    sortedPlans() {
      const sorted = this.plans.filter((x) => x.status === 'Active').slice();
      return sorted;
    },
    hourOptions() {
      return Array(12)
        .fill()
        .map((acc, idx) => moment({ hour: idx + 1 }).format('HH'));
    },
    minOptions() {
      return ['00', '30'];
    },
    endDateMin() {
      return moment(this.startDate).toDate();
    },
    timezoneOptions() {
      return moment.tz.zonesForCountry('US').map((tz) => {
        const abbr = moment.tz.zone(tz).abbr(new Date());
        return {
          label: `${abbr} - ${tz}`,
          value: tz,
        };
      });
    },
  },
  watch: {
    promo(pr) {
      if (!this.incomplete || this.finishedSection.includes('offer-type')) {
        this.startDate = pr.startDate ? moment(pr.startDate, 'YYYY-MM-DD').toDate() : new Date();

        this.endDate = pr.endDate
          ? moment(pr.endDate, 'YYYY-MM-DD').toDate()
          : moment().add(1, 'days').toDate();
        this.selectedPlans = pr.plans || [];
        this.isActive = pr.active ?? false;
        this.sendPush = pr.sendPush;
        this.sendEmail = pr.sendEmail;
        if (pr.sendTimezone) {
          this.sendTimezone = pr.sendTimezone;
        } else {
          let currentTz = 'America/Chicago';
          const webTz = moment.tz.guess();
          const isInAmerica = this.timezoneOptions.find(({ value }) => value === webTz);
          if (isInAmerica) {
            currentTz = isInAmerica.value;
          }
          this.sendTimezone = currentTz;
        }
        if (pr?.sendTime) {
          const { meridiem, hour, minute } = this.calculateSendTime(pr.sendTime);
          this.hour = hour;
          this.minute = minute;
          this.meridiem = meridiem;
        } else {
          this.hour = '12';
          this.minute = '00';
          this.meridiem = 'pm';
        }
      }
    },
    startDate(sd) {
      if (!moment(sd).isSame(this.promo?.startDate)) {
        this.setSectionIncomplete('offer-type');
      }
    },
    endDate(ed) {
      if (!moment(ed).isSame(this.promo?.endDate)) {
        this.setSectionIncomplete('offer-type');
      }
    },
    sendPush(p) {
      if (p !== this.promo?.sendPush) {
        this.setSectionIncomplete('offer-type');
      }
    },
    sendEmail(e) {
      if (e !== this.promo?.sendEmail) {
        this.setSectionIncomplete('offer-type');
      }
    },
    isActive(e) {
      if (e !== this.promo?.active) {
        this.setSectionIncomplete('offer-type');
      }
      if (this.promo?.active && !e) {
        this.endDate = moment().add(1, 'days').toDate();
      }
    },
    selectedPlans(e) {
      if (!_.isEqual(e, this.promo?.plans)) {
        this.setSectionIncomplete('offer-type');
      }
      e.forEach(async (element) => {
        if (!this.potentialUsers?.find((x) => x.planId === element)) {
          this.loading = true;
          try {
            await this.$store.dispatch('getPotensialUsers', element);
          } catch (e) {
          } finally {
            this.loading = false;
          }
        }
      });
    },
    sendTimezone(t) {
      if (t !== this.promo?.sendTimezone) {
        this.setSectionIncomplete('offer-type');
      }
    },
    hour(h) {
      const { sendTime } = this.promo;
      let promoHour = null;
      if (sendTime) {
        const { hour } = this.calculateSendTime(sendTime);
        promoHour = hour;
      }
      if (h !== promoHour) {
        this.setSectionIncomplete('offer-type');
      }
    },
    minute(m) {
      const { sendTime } = this.promo;
      let promoMin = null;
      if (sendTime) {
        const { minute } = this.calculateSendTime(sendTime);
        promoMin = minute;
      }
      if (m !== promoMin) {
        this.setSectionIncomplete('offer-type');
      }
    },
  },
  mounted() {
    let currentTz = 'America/Chicago';
    const webTz = moment.tz.guess();
    const isInAmerica = this.timezoneOptions.find(({ value }) => value === webTz);
    if (isInAmerica) {
      currentTz = isInAmerica.value;
    }
    this.sendTimezone = currentTz;
  },
  methods: {
    handleChange(key, value) {
      this[key] = value;
    },
    togglePlan(id) {
      if (this.selectedPlans?.includes(id)) {
        this.selectedPlans = this.selectedPlans.filter((x) => x !== id);
      } else {
        this.selectedPlans.push(id);
      }
    },
    calculateSendTime(sendTime) {
      let meridiem = 'am';
      let hour = '12';
      let minute = '00';
      if (sendTime.includes(':')) {
        [hour, minute] = sendTime.split(':');
        hour = Number(hour);
        minute = Number(minute);
      } else {
        [hour, minute] = sendTime;
      }
      if (hour === 0) {
        hour = 12;
      } else if (hour >= 13) {
        hour -= 12;
        meridiem = 'pm';
      } else if (hour === 12) {
        meridiem = 'pm';
      }
      if (hour < 10) {
        hour = `0${hour}`;
      } else {
        hour = `${hour}`;
      }
      if (minute < 10) {
        minute = `0${minute}`;
      } else {
        minute = `${minute}`;
      }
      return {
        hour,
        minute,
        meridiem,
      };
    },
    checkErrors() {
      const errorObj = {};
      const startDate = moment(this.startDate);
      const endDate = moment(this.endDate);
      if (endDate.isBefore(startDate) || endDate.isSame(startDate)) {
        errorObj.wrongDate = 'End date should be after start date';
      }
      return errorObj;
    },
    handleContinue() {
      const errorObj = this.checkErrors();
      this.errObj = errorObj;
      if (Object.keys(errorObj).length === 0) {
        this.changeActiveStepIdx(this.index + 1);
        let promoHour = this.hour;
        if (this.meridiem === 'pm') {
          if (promoHour < '12') {
            promoHour = `${Number(promoHour) + 12}`;
          }
        } else if (promoHour === '12') {
          promoHour = '00';
        }
        const newPromo = {
          ...this.promo,
          startDate: moment(this.startDate).format('YYYY-MM-DD'),
          endDate: moment(this.endDate).format('YYYY-MM-DD'),
          sendPush: this.sendPush,
          sendEmail: this.sendEmail,
          sendTime: `${promoHour}:${this.minute}:00`,
          sendTimezone: this.sendTimezone,
          active: this.isActive,
          plans: this.selectedPlans,
        };
        this.updatePromo(newPromo, 'offer-type');
      }
    },
  },
};
</script>

<style lang="scss">
.prb-section-container {
  .pbs-radio-container {
    margin-left: 24px;
  }
  .promo-set-time-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 24px 0 12px 0;
    .promo-set-time-label {
      font-size: 0.875em;
      color: $N90;
      margin-right: 15px;
    }
    .promo-minute-input {
      background-color: #f6f8ff !important;
      margin-right: 10px;
      font-size: 0.75em;
      width: 35px;
      height: 30px;
      text-align: center;
      background-color: $N5;
    }
    .promo-dropdown {
      background-color: $N5;
    }
  }
  .promo-select-date-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-left: 24px;
    &:not(:last-child) {
      margin-bottom: 8px;
    }
    .promo-select-date-label {
      color: $N90;
      font-size: 0.75em;
      font-weight: 500;
      width: 15%;
    }
    .pbs-date-picker {
      background: $NBLUE5;
      width: 150px;
      height: 38px;
      border-radius: 3px;
      border: unset;
      span {
        font-size: 0.75em;
      }
    }
  }
  .promo-select {
    background: $NBLUE5;
    width: 180px;
    height: 30px;
    padding-left: 8px;
    font-size: 0.75em;
    position: relative;
    &:before {
      content: '';
      position: absolute;
      right: 10px;
      top: 8px;
      width: 0;
      height: 0;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-top: 10px solid #000000;
    }
    &:after {
      content: '';
      position: absolute;
      right: 10px;
      top: 3px;
      width: 0;
      height: 0;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-top: 10px solid #333;
    }
  }
  .promo-select-date-detail-txt {
    font-size: 10px;
    font-weight: 500;
    color: $N90;
  }
  .promo-checkbox-label {
    font-weight: 300;
    font-size: 16px;
    &.disabled {
      color: $N50 !important;
    }
  }
}
</style>
